import { useState, useEffect } from "react";
import { api } from "@/lib/config";
import { Lock } from "lucide-react";
import PhotoManager from "@/components/admin/PhotoManager";
import StoryManager from "@/components/admin/StoryManager";
import EmailLog from "@/components/admin/EmailLog";

export default function AdminPage() {
  const [secret, setSecret] = useState("");
  const [authed, setAuthed] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [customTrips, setCustomTrips] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("bookings");
  const [error, setError] = useState("");

  const load = async (s) => {
    setError("");
    try {
      const headers = { "X-Admin-Secret": s };
      const [b, c, ct, st] = await Promise.all([
        api.get("/admin/bookings", { headers }),
        api.get("/admin/contacts", { headers }),
        api.get("/admin/custom-trips", { headers }),
        api.get("/admin/stats", { headers }),
      ]);
      setBookings(b.data.bookings || []);
      setContacts(c.data.messages || []);
      setCustomTrips(ct.data.leads || []);
      setStats(st.data);
      setAuthed(true);
      try { sessionStorage.setItem("apex_admin_secret", s); } catch {}
    } catch (e) {
      setError("Invalid secret or server error");
      setAuthed(false);
    }
  };

  useEffect(() => {
    let saved = null;
    try { saved = sessionStorage.getItem("apex_admin_secret"); } catch {}
    if (saved) {
      setSecret(saved);
      load(saved);
    }
    // eslint-disable-next-line
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    load(secret);
  };

  if (!authed) {
    return (
      <div data-testid="admin-login-page" className="pt-36 md:pt-40 pb-24 min-h-[80vh] bg-[#FAF6F0]">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-[#FFFFFF] rounded-3xl p-10 border border-[#E2D5C3] text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1F150E] text-[#FAF6F0] rounded-full mb-6">
              <Lock size={22} />
            </div>
            <p className="overline">admin</p>
            <h1 className="font-display text-4xl italic font-light mt-2">Admin Access</h1>
            <p className="text-[#23170E]/65 mt-3 text-sm">Enter the admin secret to view bookings.</p>
            <form onSubmit={onSubmit} className="mt-8 space-y-4">
              <input
                data-testid="admin-secret-input"
                type="password"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder="Admin secret"
                className="w-full bg-[#FAF6F0] border border-[#BCA993] rounded-full px-5 py-3 focus:outline-none focus:border-[#A6521B]"
              />
              {error && <p className="text-sm text-[#A6352B]">{error}</p>}
              <button data-testid="admin-login-btn" type="submit" className="w-full btn-pill btn-pill-primary justify-center">
                Access
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="pt-36 md:pt-40 pb-24 bg-[#FAF6F0]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <p className="overline">admin</p>
        <h1 className="font-display text-5xl italic font-light mt-3">Bookings Dashboard</h1>

        {stats && (
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { k: "Total bookings", v: stats.total_bookings },
              { k: "Pending inquiries", v: stats.inquiries },
              { k: "Custom trip leads", v: stats.custom_trip_leads ?? 0 },
              { k: "Contact messages", v: stats.contact_messages },
            ].map((s) => (
              <div key={s.k} className="bg-[#FFFFFF] border border-[#E2D5C3] rounded-3xl shadow-[0_4px_20px_-2px_rgba(35,23,14,0.06)] p-6">
                <p className="overline">{s.k}</p>
                <p className="font-marcellus text-4xl text-[#A6521B] mt-2">{s.v}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-12 flex gap-2 border-b border-[#BCA993]">
          {["bookings", "custom-trips", "contacts", "photos", "stories", "emails"].map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-6 py-3 text-sm border-b-2 transition-colors ${
                tab === t ? "border-[#A6521B] text-[#A6521B]" : "border-transparent text-[#23170E]/55 hover:text-[#23170E]"
              }`}
            >
              {t === "custom-trips" ? "Custom Trips" : t.charAt(0).toUpperCase() + t.slice(1)}{!["photos", "stories", "emails"].includes(t) && ` (${t === "bookings" ? bookings.length : t === "custom-trips" ? customTrips.length : contacts.length})`}
            </button>
          ))}
        </div>

        {tab === "bookings" && (
          <div data-testid="admin-bookings-table" className="mt-8 overflow-x-auto bg-[#FFFFFF] rounded-3xl border border-[#E2D5C3] p-2">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-[0.15em] text-[#23170E]/55 border-b border-[#BCA993]">
                <tr>
                  <th className="py-3 px-4">Ref</th>
                  <th className="py-3 px-4">Tour</th>
                  <th className="py-3 px-4">Customer</th>
                  <th className="py-3 px-4">Start</th>
                  <th className="py-3 px-4">#</th>
                  <th className="py-3 px-4">Tier</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-b border-[#E2D5C3] hover:bg-[#FAF6F0]/50">
                    <td className="py-4 px-4 font-mono text-[#A6521B]">{b.booking_reference}</td>
                    <td className="py-4 px-4">{b.tour_title}</td>
                    <td className="py-4 px-4">
                      <div>{b.full_name}</div>
                      <div className="text-xs text-[#23170E]/55">{b.email} · {b.phone}</div>
                    </td>
                    <td className="py-4 px-4">{b.start_date}</td>
                    <td className="py-4 px-4">{b.travelers}</td>
                    <td className="py-4 px-4 capitalize">{b.accommodation_tier}</td>
                    <td className="py-4 px-4">
                      <span className="px-2 py-1 text-xs uppercase tracking-[0.1em] rounded-full bg-amber-100 text-amber-900">{b.payment_status.replace(/_/g, " ")}</span>
                    </td>
                    <td className="py-4 px-4 text-xs text-[#23170E]/55">{new Date(b.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {bookings.length === 0 && (
                  <tr><td colSpan={8} className="py-12 text-center text-[#23170E]/55">No bookings yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "custom-trips" && (
          <div data-testid="admin-custom-trips" className="mt-8 space-y-4">
            {customTrips.map((c) => (
              <div key={c.id} className="bg-[#FFFFFF] border border-[#E2D5C3] rounded-3xl shadow-[0_4px_20px_-2px_rgba(35,23,14,0.06)] p-6">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-mono text-[#A6521B]">{c.lead_reference}</p>
                    <div className="font-semibold mt-1">{c.full_name}</div>
                    <div className="text-xs text-[#23170E]/55">{c.email} · {c.phone}</div>
                  </div>
                  <span className="text-xs text-[#23170E]/55">{new Date(c.created_at).toLocaleString()}</span>
                </div>
                <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
                  <div><span className="text-[#23170E]/55">Party:</span> {c.party_size}</div>
                  <div><span className="text-[#23170E]/55">Days:</span> {c.trip_length_days}</div>
                  <div><span className="text-[#23170E]/55">Month:</span> {c.preferred_month || "Flexible"}</div>
                  <div><span className="text-[#23170E]/55">Budget:</span> {c.budget_per_person}</div>
                </div>
                {c.interests && c.interests.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {c.interests.map((i) => <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-[#A6521B]/10 text-[#A6521B]">{i}</span>)}
                  </div>
                )}
                {c.notes && <p className="mt-3 text-sm text-[#23170E]/75 whitespace-pre-wrap">{c.notes}</p>}
              </div>
            ))}
            {customTrips.length === 0 && <p className="text-center text-[#23170E]/55 py-12">No custom trip leads yet.</p>}
          </div>
        )}

        {tab === "photos" && <PhotoManager secret={secret} />}

        {tab === "stories" && <StoryManager secret={secret} />}

        {tab === "emails" && <EmailLog secret={secret} />}

        {tab === "contacts" && (
          <div data-testid="admin-contacts-list" className="mt-8 space-y-4">
            {contacts.map((c) => (
              <div key={c.id} className="bg-[#FFFFFF] border border-[#E2D5C3] rounded-3xl shadow-[0_4px_20px_-2px_rgba(35,23,14,0.06)] p-6">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="font-semibold">{c.name}</div>
                    <div className="text-xs text-[#23170E]/55">{c.email}</div>
                  </div>
                  <span className="text-xs text-[#23170E]/55">{new Date(c.created_at).toLocaleString()}</span>
                </div>
                {c.subject && <div className="text-sm font-semibold mt-2">{c.subject}</div>}
                <p className="mt-2 text-sm text-[#23170E] whitespace-pre-wrap">{c.message}</p>
              </div>
            ))}
            {contacts.length === 0 && <p className="text-center text-[#23170E]/55 py-12">No contact messages yet.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
